import React, { useState, useCallback, useMemo, useRef } from "react";
import { SearchParams, MuseumObject } from "../../backend/types/domain";
import { TimelineBucket } from "../../backend/lib/timeline";
import { ApiResponse } from "../../shared/types";
import { trackedFetch, trackSearchComplete } from "../lib/analytics";

export const BACKEND_API_URL = window.location.href.includes("localhost")
  ? "http://localhost:3000/api"
  : "/api"; // Netlify Functions

class MuseumSearchClient {
  private baseUrl: string;

  constructor(baseUrl: string = BACKEND_API_URL) {
    this.baseUrl = baseUrl;
  }

  async search(
    searchParams: SearchParams,
    museums?: string[],
  ): Promise<ApiResponse> {
    const body = {
      searchParams,
      museums,
    };

    const response = await trackedFetch("search", `${this.baseUrl}/search`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  async fetchTimeline(
    searchParams: SearchParams,
  ): Promise<{ buckets: TimelineBucket[]; total: number }> {
    const response = await trackedFetch(
      "timeline",
      `${this.baseUrl}/search/timeline`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // The date range is deliberately not sent: the histogram is the
        // control for picking one, so it has to keep describing the whole axis.
        body: JSON.stringify({ searchParams }),
      },
    );

    if (!response.ok) {
      throw new Error(`Timeline request failed: ${response.statusText}`);
    }

    const data = await response.json();
    return { buckets: data.buckets ?? [], total: data.total ?? 0 };
  }

  async getAvailableMuseums(): Promise<string[]> {
    const response = await trackedFetch("museums", `${this.baseUrl}/museums`);
    if (!response.ok) {
      throw new Error(`Failed to fetch museums: ${response.statusText}`);
    }
    const data = await response.json();
    return data.museums;
  }

  async getArtifactById(id: string): Promise<MuseumObject | null> {
    // A 404 here is a shared link to an artifact we no longer have; it is
    // reported like any other failure, because the visitor sees nothing.
    const response = await trackedFetch(
      "artifact",
      `${this.baseUrl}/artifact/${id}`,
    );
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error(`Failed to fetch artifact: ${response.statusText}`);
    }
    const data = await response.json();
    return data.artifact;
  }
}

export const useMuseumSearch = () => {
  const [client] = useState(() => new MuseumSearchClient());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allResults, setAllResults] = useState<ApiResponse[]>([]);
  const [museums, setMuseums] = useState<string[]>([]);

  const search = useCallback(
    async (
      params: SearchParams,
      targetMuseums?: string[],
      prependResults: boolean = false,
    ) => {
      setLoading(true);
      setError(null);

      try {
        const startedAt = performance.now();
        const response = await client.search(params, targetMuseums);
        setAllResults((results) =>
          prependResults ? [response, ...results] : [...results, response],
        );
        trackSearchComplete({
          search_term: params.semanticQuery,
          results_page: params.page ?? 1,
          result_count: Object.values(response.results ?? {})
            .filter((result) => result.success)
            .reduce((n, result) => n + (result.objects?.length ?? 0), 0),
          duration_ms: Math.round(performance.now() - startedAt),
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Search failed");
        setAllResults([]);
      } finally {
        setLoading(false);
      }
    },
    [client],
  );

  const [timeline, setTimeline] = useState<TimelineBucket[]>([]);
  const [timelineTotal, setTimelineTotal] = useState(0);
  const [timelineLoading, setTimelineLoading] = useState(false);
  // Monotonic request id: histogram fetches are fired on every filter change
  // and can land out of order, so a slow early response must not overwrite a
  // fast later one.
  const timelineRequestId = useRef(0);

  const loadTimeline = useCallback(
    async (params: SearchParams) => {
      const requestId = ++timelineRequestId.current;
      setTimelineLoading(true);
      try {
        const { buckets, total } = await client.fetchTimeline(params);
        if (requestId === timelineRequestId.current) {
          setTimeline(buckets);
          setTimelineTotal(total);
        }
      } catch (err) {
        // A missing histogram degrades the scrubber to a plain range control;
        // it should never take the search results down with it.
        console.warn("Timeline histogram unavailable", err);
      } finally {
        if (requestId === timelineRequestId.current) setTimelineLoading(false);
      }
    },
    [client],
  );

  const loadMuseums = useCallback(async () => {
    try {
      const availableMuseums = await client.getAvailableMuseums();
      setMuseums(availableMuseums);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load museums");
    }
  }, [client]);

  const fetchArtifactById = useCallback(
    async (id: string): Promise<MuseumObject | null> => {
      try {
        return await client.getArtifactById(id);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch artifact");
        return null;
      }
    },
    [client],
  );

  const prependArtifact = useCallback((artifact: MuseumObject) => {
    const syntheticResponse: ApiResponse = {
      searchParams: {},
      results: {
        deepLink: {
          museum: "deepLink",
          url: "",
          success: true,
          objects: [artifact],
          count: 1,
        },
      },
      timestamp: new Date().toISOString(),
    };
    setAllResults((results) => [syntheticResponse, ...results]);
  }, []);

  const getAllObjects = useCallback((): MuseumObject[] => {
    if (!allResults) return [];

    return allResults.flatMap((result) =>
      Object.values(result.results)
        .filter((result) => result.success && result.objects)
        .flatMap((result) => result.objects || []),
    );
  }, [allResults]);

  const consolidatedSummary = useMemo(() => {
    if (!allResults) return null;

    const summary = allResults.reduce(
      (acc, result) => {
        if (!result.summary) return acc;
        return {
          successfulSearches:
            acc.successfulSearches + result.summary.successfulSearches,
          failedSearches: acc.failedSearches + result.summary.failedSearches,

          // number should stay constant
          totalResults: result.summary.totalResults || 0,
          totalMuseums: result.summary.totalMuseums || 0,
        };
      },
      {
        totalResults: 0,
        successfulSearches: 0,
        failedSearches: 0,
        totalMuseums: 0,
      },
    );

    return summary;
  }, [allResults]);

  return {
    search,
    loadTimeline,
    timeline,
    timelineTotal,
    timelineLoading,
    loadMuseums,
    fetchArtifactById,
    prependArtifact,
    getAllObjects,
    loading,
    error,
    results: allResults,
    clearResults: () => setAllResults([]),
    museums,
    summary: consolidatedSummary,
  };
};
